import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Sync as SyncIcon,
  Save as SaveIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import reorderQuantityService, {
  ReorderQuantity,
  ReorderQuantityFilters,
  ReorderQuantityUpdate,
  Warehouse,
  Category
} from '../../services/reorderQuantityService';
import { useReorderQuantityPermissions } from '../../hooks/useReorderQuantityPermissions';

interface ChangedQuantity {
  reorderQuantityId: string;
  warehouseId: string;
  originalQuantity: number;
  newQuantity: number;
}

const ReorderQuantitiesPage: React.FC = () => {
  // Permissions
  const {
    hasAccessPermission,
    hasSyncPermission,
    hasDownloadPermission,
    hasUploadPermission,
    hasSavePermission,
    hasViewPermission
  } = useReorderQuantityPermissions();

  // State management
  const [reorderQuantities, setReorderQuantities] = useState<ReorderQuantity[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Filters and pagination
  const [filters, setFilters] = useState<ReorderQuantityFilters>({
    page: 1,
    limit: 20,
    search: '',
    category: '',
    warehouse: '',
    sortBy: 'productName',
    sortOrder: 'asc'
  });
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Changed quantities tracking
  const [changedQuantities, setChangedQuantities] = useState<Map<string, ChangedQuantity>>(new Map());

  // UI state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);

  // Load data
  const loadReorderQuantities = useCallback(async () => {
    try {
      setLoading(true);
      const response = await reorderQuantityService.getReorderQuantities(filters);
      setReorderQuantities(response.data);
      setTotalPages(response.pagination.pages);
      setTotalItems(response.pagination.total);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadWarehouses = async () => {
    try {
      const response = await reorderQuantityService.getWarehouses();
      setWarehouses(response.data);
    } catch (error: any) {
      console.error('Error loading warehouses:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await reorderQuantityService.getCategories();
      setCategories(response.data);
    } catch (error: any) {
      console.error('Error loading categories:', error);
    }
  };

  useEffect(() => {
    loadReorderQuantities();
  }, [loadReorderQuantities]);

  useEffect(() => {
    loadWarehouses();
    loadCategories();
  }, []);

  // Handle quantity change
  const handleQuantityChange = (
    reorderQuantityId: string,
    warehouseId: string,
    originalQuantity: number,
    newQuantity: number
  ) => {
    const key = `${reorderQuantityId}-${warehouseId}`;
    const newChangedQuantities = new Map(changedQuantities);

    if (newQuantity === originalQuantity) {
      // Remove from changed quantities if back to original
      newChangedQuantities.delete(key);
    } else {
      // Add or update changed quantity
      newChangedQuantities.set(key, {
        reorderQuantityId,
        warehouseId,
        originalQuantity,
        newQuantity
      });
    }

    setChangedQuantities(newChangedQuantities);

    // Update the local state
    setReorderQuantities(prev => prev.map(rq => {
      if (rq._id === reorderQuantityId) {
        return {
          ...rq,
          warehouseReorderQuantities: rq.warehouseReorderQuantities.map(wrq => {
            if (wrq.warehouse._id === warehouseId) {
              return { ...wrq, reorderQuantity: newQuantity };
            }
            return wrq;
          })
        };
      }
      return rq;
    }));
  };

  // Save changes
  const handleSaveChanges = async () => {
    if (changedQuantities.size === 0) {
      setSnackbar({
        open: true,
        message: 'لا توجد تغييرات للحفظ',
        severity: 'warning'
      });
      return;
    }

    try {
      setSaving(true);
      const updates: ReorderQuantityUpdate[] = Array.from(changedQuantities.values()).map(change => ({
        reorderQuantityId: change.reorderQuantityId,
        warehouseId: change.warehouseId,
        newQuantity: change.newQuantity
      }));

      const response = await reorderQuantityService.updateReorderQuantities(updates);
      
      setSnackbar({
        open: true,
        message: response.message,
        severity: 'success'
      });

      // Clear changed quantities
      setChangedQuantities(new Map());

      // Reload data to get updated timestamps
      await loadReorderQuantities();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Sync with products and warehouses
  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await reorderQuantityService.syncReorderQuantities();
      
      setSnackbar({
        open: true,
        message: response.message,
        severity: 'success'
      });

      // Reload data
      await loadReorderQuantities();
      setSyncDialogOpen(false);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setSyncing(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof ReorderQuantityFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value // Reset to first page when changing filters
    }));
  };

  // Handle search
  const handleSearch = () => {
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  // Export current quantities to Excel
  const handleExportQuantities = async () => {
    try {
      setLoading(true);
      
      // Use axios with proper authentication headers
      const response = await fetch('/api/reorder-quantities/export', {
        method: 'GET',
        headers: {
          'x-auth-token': localStorage.getItem('token') || '',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-branch-id': localStorage.getItem('activeBranchId') || 'default'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'خطأ في تصدير الكميات');
      }

      // Get the blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'reorder-quantities.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSnackbar({
        open: true,
        message: 'تم تصدير الكميات بصيغة Excel بنجاح',
        severity: 'success'
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'خطأ في تصدير الكميات',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Import updated quantities from Excel
  const handleImportQuantities = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      let excelData: any[] = [];

      // Check file type and read accordingly
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        // Read Excel file
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        excelData = XLSX.utils.sheet_to_json(worksheet);
      } else if (file.name.endsWith('.csv')) {
        // Read CSV file
        excelData = await readCSVFile(file);
      } else {
        throw new Error('نوع الملف غير مدعوم. يرجى استخدام ملفات Excel (.xlsx, .xls) أو CSV (.csv)');
      }
      
      if (excelData.length === 0) {
        throw new Error('الملف فارغ أو غير صالح');
      }

      // تسجيل البيانات للتشخيص
      console.log('Frontend - Excel data sample:', excelData[0]);
      console.log('Frontend - Total rows:', excelData.length);

      const response = await reorderQuantityService.importUpdatedQuantities(excelData);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || 'تم استيراد الكميات بنجاح',
          severity: 'success'
        });
        
        // إعادة تحميل البيانات
        loadReorderQuantities();
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'خطأ في استيراد الكميات',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      // إعادة تعيين قيمة input
      event.target.value = '';
    }
  };

  // Helper function to convert data to CSV
  const convertToCSV = (data: any[]) => {
    if (data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvHeaders = headers.join(',');
    const csvRows = data.map(row =>
      headers.map(header => {
        const value = row[header];
        // إضافة علامات اقتباس للنصوص التي تحتوي على فواصل
        return typeof value === 'string' && value.includes(',')
          ? `"${value}"`
          : value;
      }).join(',')
    );
    
    return [csvHeaders, ...csvRows].join('\n');
  };

  // Helper function to download CSV
  const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper function to read CSV file
  const readCSVFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter(line => line.trim());
          
          if (lines.length < 2) {
            reject(new Error('الملف يجب أن يحتوي على عنوان وسطر واحد على الأقل'));
            return;
          }
          
          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
          const data = lines.slice(1).map(line => {
            const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
            const row: any = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            return row;
          });
          
          resolve(data);
        } catch (error) {
          reject(new Error('خطأ في قراءة الملف'));
        }
      };
      reader.onerror = () => reject(new Error('خطأ في قراءة الملف'));
      reader.readAsText(file, 'UTF-8');
    });
  };

  // Format variant attributes
  const formatVariantAttributes = (attributes: Record<string, string>) => {
    if (!attributes || typeof attributes !== 'object') {
      return null;
    }
    return Object.entries(attributes).map(([key, value]) => (
      <Chip
        key={key}
        label={`${key}: ${value}`}
        size="small"
        variant="outlined"
        sx={{ margin: 0.25 }}
      />
    ));
  };

  // Check if quantity has changed
  const isQuantityChanged = (reorderQuantityId: string, warehouseId: string) => {
    return changedQuantities.has(`${reorderQuantityId}-${warehouseId}`);
  };

  // التحقق من صلاحية الوصول إلى الصفحة
  if (!hasAccessPermission()) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          ليس لديك صلاحية للوصول إلى هذه الصفحة
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          كميات إعادة طلب للمنتجات
        </Typography>
        <Typography variant="body1" color="text.secondary">
          إدارة كميات إعادة الطلب لجميع المنتجات والمتغيرات في المستودعات
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="البحث في المنتجات"
              placeholder="اسم المنتج، الباركود، أو SKU"
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                )
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>التصنيف</InputLabel>
              <Select
                value={filters.category || ''}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                label="التصنيف"
              >
                <MenuItem value="">جميع التصنيفات</MenuItem>
                {categories && categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>المستودع</InputLabel>
              <Select
                value={filters.warehouse || ''}
                onChange={(e) => handleFilterChange('warehouse', e.target.value)}
                label="المستودع"
              >
                <MenuItem value="">جميع المستودعات</MenuItem>
                {warehouses && warehouses.map((warehouse) => (
                  <MenuItem key={warehouse._id} value={warehouse._id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={handleSearch}
            >
              بحث
            </Button>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={() => setSyncDialogOpen(true)}
              disabled={syncing || !hasSyncPermission()}
            >
              مزامنة
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Excel Import/Export Actions */}
      <Card sx={{ mb: 3, bgcolor: 'info.light' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ color: 'info.contrastText' }}>
                📊 إدارة الكميات عبر الإكسل
              </Typography>
              <Typography variant="body2" sx={{ color: 'info.contrastText', opacity: 0.8 }}>
                حمل الكميات الحالية، عدلها في الإكسل، ثم ارفعها مرة أخرى
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleExportQuantities}
                disabled={loading || !hasDownloadPermission()}
                sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
              >
                تحميل الكميات الحالية
              </Button>
              
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadIcon />}
                disabled={loading || !hasUploadPermission()}
                sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                رفع الملف المحدث
                <input
                  type="file"
                  hidden
                  accept=".csv,.xlsx,.xls"
                  onChange={handleImportQuantities}
                />
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Summary Card */}
      {changedQuantities.size > 0 && (
        <Card sx={{ mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              ملخص التغييرات
            </Typography>
            <Typography variant="body2">
              تم تعديل كميات إعادة الطلب لـ {changedQuantities.size} متغير
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveChanges}
                disabled={saving || !hasSavePermission()}
                sx={{ bgcolor: 'success.dark' }}
              >
                {saving ? <CircularProgress size={20} /> : 'حفظ جميع التغييرات'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            قائمة المنتجات وكميات إعادة الطلب
          </Typography>
          <Typography variant="body2" color="text.secondary">
            إجمالي المتغيرات: {totalItems}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>الرقم التسلسلي</TableCell>
                <TableCell>اسم المنتج</TableCell>
                <TableCell>المتغير</TableCell>
                <TableCell>الباركود</TableCell>
                <TableCell>الوحدة</TableCell>
                {warehouses && warehouses.map((warehouse) => (
                  <TableCell key={warehouse._id} align="center">
                    {warehouse.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {reorderQuantities && (() => {
                // Group reorder quantities by product
                const groupedByProduct = reorderQuantities.reduce((acc, rq) => {
                  // التحقق من وجود المنتج قبل الوصول إلى خصائصه
                  if (!rq.product || !rq.product._id) {
                    console.warn('تحذير: منتج بدون معرف:', rq);
                    return acc;
                  }
                  
                  const productId = rq.product._id;
                  if (!acc[productId]) {
                    acc[productId] = {
                      product: rq.product,
                      productName: rq.productName,
                      variants: []
                    };
                  }
                  acc[productId].variants.push(rq);
                  return acc;
                }, {} as Record<string, { product: any; productName: string; variants: typeof reorderQuantities }>);

                // Sort variants within each product group by variantSerialNumber
                Object.values(groupedByProduct).forEach(group => {
                  group.variants.sort((a, b) => a.variantSerialNumber - b.variantSerialNumber);
                });

                return Object.values(groupedByProduct).map((group) => (
                  <React.Fragment key={group.product._id}>
                    {/* Product header row */}
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell colSpan={5 + (warehouses?.length || 0)} sx={{ fontWeight: 'bold', fontSize: '1.1em', py: 1 }}>
                        📦 {group.productName}
                      </TableCell>
                    </TableRow>
                    {/* Variant rows */}
                    {group.variants.map((rq) => (
                      <TableRow key={rq._id} hover>
                        <TableCell sx={{ paddingLeft: 4 }}>
                          <Chip
                            label={rq.variantSerialNumber}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            متغير
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {formatVariantAttributes(rq.variantAttributes)}
                          </Box>
                        </TableCell>
                        <TableCell>{rq.barcode}</TableCell>
                        <TableCell>{rq.unit}</TableCell>
                        {warehouses && warehouses.map((warehouse) => {
                          const warehouseQuantity = rq.warehouseReorderQuantities && rq.warehouseReorderQuantities.find(
                            wrq => wrq.warehouse._id === warehouse._id
                          );
                          const originalQuantity = warehouseQuantity?.reorderQuantity || 0;
                          const isChanged = isQuantityChanged(rq._id, warehouse._id);

                          return (
                            <TableCell key={warehouse._id} align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={warehouseQuantity?.reorderQuantity || 0}
                                onChange={(e) => {
                                  const newValue = parseInt(e.target.value) || 0;
                                  handleQuantityChange(
                                    rq._id,
                                    warehouse._id,
                                    originalQuantity,
                                    newValue
                                  );
                                }}
                                inputProps={{ min: 0, style: { textAlign: 'center' } }}
                                sx={{
                                  width: 80,
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: isChanged ? 'warning.light' : 'transparent',
                                    '& fieldset': {
                                      borderColor: isChanged ? 'warning.main' : undefined
                                    }
                                  }
                                }}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </React.Fragment>
                ));
              })()}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalItems}
          page={(filters.page || 1) - 1}
          onPageChange={(_, newPage) => handleFilterChange('page', newPage + 1)}
          rowsPerPage={filters.limit || 20}
          onRowsPerPageChange={(e) => {
            const value = e.target.value;
            // إذا اختار "الكل"، نستخدم -1 كقيمة خاصة
            handleFilterChange('limit', value === '-1' ? -1 : parseInt(value));
          }}
          rowsPerPageOptions={[
            { label: '10', value: 10 },
            { label: '20', value: 20 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
            { label: 'الكل', value: -1 }
          ]}
          labelRowsPerPage="عدد الصفوف في الصفحة:"
          labelDisplayedRows={({ from, to, count }) => {
            // إذا كان limit = -1 (الكل)، نعرض "1-{count} من {count}"
            if (filters.limit === -1) {
              return `1-${count} من ${count}`;
            }
            return `${from}-${to} من ${count}`;
          }}
        />
      </TableContainer>

      {/* Sync Dialog */}
      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)}>
        <DialogTitle>مزامنة كميات إعادة الطلب</DialogTitle>
        <DialogContent>
          <Typography>
            هذه العملية ستقوم بمزامنة كميات إعادة الطلب مع المنتجات والمستودعات الحالية.
            سيتم إنشاء سجلات جديدة للمنتجات التي لا تحتوي على كميات إعادة طلب.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)}>إلغاء</Button>
          <Button
            onClick={handleSync}
            variant="contained"
            disabled={syncing}
            startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
          >
            مزامنة
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReorderQuantitiesPage;